import type { Agent, Region } from "@fingerprintjs/fingerprintjs-pro"
import getFingerprintProPublicKey, {
  type FingerprintProPublicKeyData
} from "@/services/getFingerprintProPublicKey"
import fingerprintProTracker from "@/services/fingerprintProTracker"

const DEFAULT_FINGERPRINT_PRO_REGION: Region = "ap"

const FPP_ALERT_PREFIX = "[9W FPP Trial License][Alert]"
const FPP_INFO_PREFIX = "[9W FPP Trial License]"
const FPP_RATE_LIMIT_MSG = `${FPP_ALERT_PREFIX} Public Key: too many requests.`
const FPP_QUOTA_REACHED_MSG = `${FPP_INFO_PREFIX} Web & iOS monthly quota reached.`

// Raw error strings emitted by FingerprintJS Pro SDK; matched against to
// classify quota / rate-limit signals before forwarding to TG.
const SDK_RATE_LIMIT_SIGNATURE = "Too many requests, rate limit exceeded"
const SDK_SUBSCRIPTION_INACTIVE_SIGNATURE = "SubscriptionNotActive"

const isFingerprintProRegion = (value: string): value is Region =>
  value === "us" || value === "eu" || value === "ap"

const resolveRegion = (): Region => {
  const envRegion = import.meta.env.VITE_FINGERPRINT_PRO_REGION
  return envRegion && isFingerprintProRegion(envRegion)
    ? envRegion
    : DEFAULT_FINGERPRINT_PRO_REGION
}

const resolvePublicKeyData = async (): Promise<
  FingerprintProPublicKeyData | undefined
> => {
  try {
    return await getFingerprintProPublicKey.handler()
  } catch (error) {
    console.warn("getFingerprintProPublicKey failed", error)
    return undefined
  }
}

let cachedAgent: Agent | undefined

const initAgent = async (apiKey: string): Promise<Agent> => {
  if (cachedAgent) return cachedAgent

  const { load } = await import("@fingerprintjs/fingerprintjs-pro")
  cachedAgent = await load({ apiKey, region: resolveRegion() })

  return cachedAgent
}

const extractRawErrorMsg = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error

  try {
    return JSON.stringify(error)
  } catch {
    return "Unknown FingerprintJS Pro error"
  }
}

// Map raw SDK errors to TG-formatted messages defined in SACRIC-1187 spec §2.
// Backend forwards `errorMsg` verbatim to Telegram, so the prefix and wording
// must match the spec table.
const formatTrackerErrorMsg = (rawErrorMsg: string): string => {
  if (rawErrorMsg.includes(SDK_RATE_LIMIT_SIGNATURE)) return FPP_RATE_LIMIT_MSG
  if (rawErrorMsg.includes(SDK_SUBSCRIPTION_INACTIVE_SIGNATURE))
    return FPP_QUOTA_REACHED_MSG
  return `${FPP_ALERT_PREFIX} ${rawErrorMsg}`
}

interface FingerprintProSDKResult {
  eventId?: string
  errorMsg?: string
}

const runSDK = async (
  apiKey: string,
  linkedId: string
): Promise<FingerprintProSDKResult> => {
  try {
    const agent = await initAgent(apiKey)
    const result = await agent.get({ linkedId, tag: { data: "test" } })

    return { eventId: result.requestId }
  } catch (error) {
    console.warn("FingerprintJS Pro SDK failed", error)

    return { errorMsg: formatTrackerErrorMsg(extractRawErrorMsg(error)) }
  }
}

export const runFingerprintProFlow = async (userID: string) => {
  const publicKeyData = await resolvePublicKeyData()
  if (!publicKeyData) return undefined

  const { publicKey, siteId, createdDate } = publicKeyData
  const linkedId = `${siteId}_${userID}_${createdDate}`
  const { eventId, errorMsg } = await runSDK(publicKey, linkedId)

  return fingerprintProTracker.handler(
    eventId
      ? { eventId, publicKey }
      : { errorMsg: errorMsg ?? `${FPP_ALERT_PREFIX} Unknown FingerprintJS Pro error` }
  )
}

export default runFingerprintProFlow
